/* ==========================================================================
   Self-sizing fields — a textarea is as tall as the text inside it
   Avanti Design Rules / ui/self-sizing-fields

   Dependency-free, no build step.

     autosize('textarea[data-autosize]');            // one call, whole page
     autosize(el, { minRows: 3, maxRows: 14 });      // or one element

   Per-element overrides live on the element, so markup stays the source of
   truth and no selector list has to be kept in sync:

     <textarea data-autosize data-min-rows="3" data-max-rows="14"></textarea>

   Returns a handle: `{ fit(), destroy(), elements }`. Call `fit()` after you
   set `.value` from code — assigning `value` fires no event, and a drafted
   line arriving from a server is exactly the case this method exists for.
   ========================================================================== */

const DEFAULTS = {
  minRows: 2,     // height at rest, empty
  maxRows: 18,    // where growing stops and scrolling starts
  observe: true,  // refit when the column width changes
};

const num = (v, fallback) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * The whole method in one function.
 *
 * Two subtleties, both of which produce the bug this exists to remove if you
 * skip them:
 *
 *  1. `height: auto` first. `scrollHeight` reports the content height *or* the
 *     current box height, whichever is larger, so measuring without releasing
 *     the height makes the field a one-way ratchet that never shrinks.
 *  2. `scrollHeight` counts padding but never borders. Under `border-box` the
 *     CSS height has to carry both, so the raw number leaves the field a
 *     border-width short — and a field one pixel short of its content scrolls,
 *     which is precisely the state we are removing.
 */
function fitOne(el) {
  const cfg = el.__autosize || DEFAULTS;
  const cs = getComputedStyle(el);

  // `line-height: normal` does not resolve to a number in any browser.
  const line = num(cs.lineHeight, num(cs.fontSize, 16) * 1.5);
  const pads = num(cs.paddingTop, 0) + num(cs.paddingBottom, 0);
  const borders = num(cs.borderTopWidth, 0) + num(cs.borderBottomWidth, 0);
  const borderBox = cs.boxSizing === 'border-box';
  const chrome = borderBox ? pads + borders : 0;

  const prev = el.style.height;
  el.style.height = 'auto';
  const wanted = borderBox ? el.scrollHeight + borders : el.scrollHeight - pads;

  // A field measured while its container is `display: none` reports 0. Leave
  // the height it had rather than collapsing it to nothing.
  if (wanted <= chrome && el.value) {
    el.style.height = prev;
    return;
  }

  const min = line * cfg.minRows + chrome;
  const max = line * cfg.maxRows + chrome;

  el.style.height = `${Math.min(Math.max(wanted, min), max)}px`;
  // Hidden below the cap, so no scrollbar gutter appears in the common case.
  el.style.overflowY = wanted > max ? 'auto' : 'hidden';
}

function autosize(target, options = {}) {
  const elements =
    typeof target === 'string'
      ? Array.from(document.querySelectorAll(target))
      : target instanceof Element
        ? [target]
        : Array.from(target || []);

  const fields = elements.filter((el) => el instanceof HTMLTextAreaElement);

  for (const el of fields) {
    el.__autosize = {
      minRows: num(el.dataset.minRows, options.minRows ?? DEFAULTS.minRows),
      maxRows: num(el.dataset.maxRows, options.maxRows ?? DEFAULTS.maxRows),
    };
    // `rows` still governs the first paint, before any script runs — set it to
    // the resting height so the field never visibly jumps on load.
    el.rows = el.__autosize.minRows;
    el.style.resize = 'none';   // it sizes itself; a handle would only fight it
    el.addEventListener('input', onInput);
  }

  const fit = () => fields.forEach(fitOne);

  // Width changes rewrap the text without changing it, and a late webfont
  // changes the line height. Both need a refit and neither fires `input`.
  //
  // Width *only*: the observer watches an element whose height we are about to
  // change, so reacting to height would be a loop that reports itself.
  const widths = new WeakMap();
  const onResize = (entries) => {
    let rewrapped = false;
    for (const entry of entries) {
      const w = entry.contentRect.width;
      if (widths.get(entry.target) !== w) {
        widths.set(entry.target, w);
        rewrapped = true;
      }
    }
    if (rewrapped) fit();
  };
  const ro =
    options.observe !== false && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(onResize)
      : null;
  if (ro) fields.forEach((el) => ro.observe(el));
  else window.addEventListener('resize', fit);
  if (document.fonts) document.fonts.ready.then(fit).catch(() => {});

  fit();

  return {
    elements: fields,
    fit,
    destroy() {
      fields.forEach((el) => {
        el.removeEventListener('input', onInput);
        el.style.height = '';
        el.style.overflowY = '';
        el.style.resize = '';
        delete el.__autosize;
      });
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', fit);
    },
  };
}

function onInput(e) {
  fitOne(e.currentTarget);
}

/* Classic script by default (`<script src="autosize.js">`), so a single-file
   page works from file://. For modules, append `export { autosize };` and load
   with `<script type="module">`. */
if (typeof window !== 'undefined') {
  window.autosize = autosize;
}

export { autosize };
